import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { ContactModel } from '../../components/contact.model';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { SmsService } from '../sms.service';
import { ListViewSettingsService } from '../../utilities/fi-services/list-view-settings.service';
import { getDefaultCountryId } from '../../core/country-id';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { isDefined } from '@fi-shared/utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { ISMSMessageContact, SmsModel } from '@fi-app/sms/sms.model';
import { UserModel } from '@fi-app/user/user.model';
import { ListViewSettingModel } from '@fi-app/components/items-list/list-view-setting.model';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { EditTabsComponent } from '@fi-app/utilities/edit-tabs/edit-tabs.component';
import { ILightWeightObject } from '@fi-shared/models';
import { ISMSEdit } from '@fi-app/sms/sms';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { removeItem } from '@fi-shared/utils/remove-item';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiSmsUsersDialogComponent } from '@fi-app/sms/fi-sms-users-dialog/fi-sms-users-dialog.component';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { uniqueBy } from '@fi-shared/utils/fi-unique-by';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';
import { FiCountryHelperService } from '@fi-shared/fi-contacts/fi-countries-select/country-helper.service';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { IContactModel } from '@fi-shared/models/common';
import { IUserListItemModel, IUserListItemViewModel } from '@fi-app/user/user';

@Component({
    selector: 'fi-sms-edit',
    templateUrl: './sms-edit.component.html',
    styleUrls: ['./sms-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-sms-edit' },
    standalone: false,
})
export class FiSmsEditComponent implements OnInit, OnDestroy {
    @Input() sms: SmsModel;
    @Input() currentUser: UserModel;
    @ViewChild('editTabsComponent') editTabsComponent: EditTabsComponent;

    EditTabs: EditTabsModel;
    ObjectTypes = ObjectTypes;
    smsEdit: ISMSEdit = {
        PhoneNumberContacts: [],
        UserContacts: [],
    };

    sendersData = new DropdownSettingsModelV2({
        dataTextField: 'value',
        dataValueField: 'value',
    });

    private readonly SMSCharsCount = 160;
    SMSCountHint$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    constructor(
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private smsService: SmsService,
        private countryHelperService: FiCountryHelperService,
        private listViewSettingsService: ListViewSettingsService,
        private stateService: FiStateService,
        private saveChangesWarningService: SaveChangesWarningService,
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private cdr: ChangeDetectorRef,
        private detectDevicesService: DetectDevicesService,
        private fiContactsService: FiContactsService,
        private fiDialogsService: FiDialogsService,
    ) {}

    ngOnInit() {
        this.setPageData();
        this.setInitialSMSData();
        this.setSMSCountHint();
    }

    private setPageData() {
        this.EditTabs = new EditTabsModel(
            this.appTextService.get(this.sms.Id ? 'sms_object_name' : 'new_sms'),
            this.appTextService.get('common.cancel'),
            this.appTextService.get('common.send'),
        );
        this.EditTabs.Tabs.push(new EditTabModel(undefined, undefined));
    }

    private setInitialSMSData() {
        const senders = this.userDataService
            .getUserBusinessUnits()
            .map((item: ILightWeightObject) => ({ value: item.Name }))
            .concat(this.customerDataService.customerData.Senders.map((item: string) => ({ value: item })));
        this.currentUser.PhoneNumberContacts.forEach((phoneNumber: IContactModel) => {
            const value: string = this.fiContactsService.getFullContactValue(phoneNumber).replace(/[ \(\))]/g, '');
            if (value) {
                senders.push({ value: value });
            }
        });

        this.sendersData.setDataSet(senders);
        this.sms.Sender = this.customerDataService.customerData.Senders.length
            ? this.customerDataService.customerData.Senders[0]
            : this.userDataService.userData.BusinessUnitName;

        if (this.stateService.current.name == FiStateNames.SMS.Copy) {
            this.sms.Contacts.forEach((contact) => {
                if (!contact.ContactType) {
                    this.smsEdit.PhoneNumberContacts.push(contact);
                } else {
                    contact.Id = contact.OriginalContactId;
                    delete contact.OriginalContactId;
                    this.smsEdit.UserContacts.push(contact);
                }
            });
            delete this.sms.Contacts;
        }

        if (!this.smsEdit.PhoneNumberContacts.length) {
            this.addNewPhoneNumber();
        }

        this.updateSendToUserContactIds();

        const getModelData = () => {
            return {
                sms: this.sms,
                smsEdit: this.smsEdit,
            };
        };
        this.saveChangesWarningService.start(getModelData);
    }

    addNewPhoneNumber() {
        const defaultContact = this.currentUser.PhoneNumberContacts.find((contact) => contact.IsDefault);
        const defaultCountryId = isDefined(defaultContact)
            ? defaultContact.CountryId
            : getDefaultCountryId(this.userDataService.applicationLanguage);

        this.smsEdit.PhoneNumberContacts.push(
            new ContactModel({
                ContactType: FiContactInformationTypes.WorkPhoneNumber,
                CountryId: defaultCountryId,
                IsDefault: true,
            }),
        );
    }

    removePhoneNumber(phoneNumberContact) {
        removeItem(this.smsEdit.PhoneNumberContacts, phoneNumberContact);
    }

    private updateSendToUserContactIds() {
        this.sms.SendToUserContactIds = this.smsEdit.UserContacts.map((value) => value.Id);
    }

    onUserContactRemoveItem(contact) {
        removeItem(this.smsEdit.UserContacts, contact);
        this.updateSendToUserContactIds();
    }

    setSMSCountHint() {
        this.SMSCountHint$.next(this.getSMSCountText());
    }

    private getSmsFullText(): string {
        return this.sms.Text || '';
    }

    private getSMSCountText(): string {
        const totalChars = this.getSmsFullText().length;
        const SMSCount = Math.ceil(totalChars / this.SMSCharsCount);
        return this.appTextService.get('sms.chars_left').format({
            '{chars_left}': SMSCount * this.SMSCharsCount - totalChars,
            '{sms_count}': SMSCount,
        });
    }

    chooseUsersNew() {
        this.listViewSettingsService.getSetting(ObjectTypes.User).then((userListViewSettings: ListViewSettingModel) => {
            this.fiDialogsService
                .custom({
                    isTopCloseButtonShown: true,
                    isNoButtonHidden: true,
                    isNewButtonsUI: true,
                    yesButtonText: this.appTextService.get('insert'),
                    contentComponentData: userListViewSettings,
                    contentComponentRef: FiSmsUsersDialogComponent,
                    config: {
                        minWidth: '80%',
                        maxWidth: '80%',
                        autoFocus: false,
                    },
                })
                .onYesButtonClick((result: IUserListItemViewModel[]) => {
                    const userFlatten: IUserListItemViewModel[] = fiItemsFlatten(result);
                    const uniqueUsersSelection = uniqueBy(userFlatten, 'Id');
                    const notAddedUsers = uniqueUsersSelection.filter((uniqueUser: IUserListItemViewModel) => {
                        return !this.smsEdit.UserContacts.find((userContact) => userContact.Id === uniqueUser.Id);
                    });

                    this.smsEdit.UserContacts = [...this.smsEdit.UserContacts, ...notAddedUsers];
                    this.cdr.detectChanges();
                    this.updateSendToUserContactIds();
                });
        });
    }

    send() {
        const loading = this.loadingService.startLoading();
        this.sms.SendToPhoneNumberContacts.length = 0;
        this.countryHelperService.getCountryCodes().then((countries: ICountryModel[]) => {
            this.smsEdit.PhoneNumberContacts.forEach((contact: ISMSMessageContact) => {
                if (!contact.ContactValue) {
                    return;
                }
                const country: ICountryModel = countries.find(
                    (country: ICountryModel) => country.Id === contact.CountryId,
                );
                this.sms.SendToPhoneNumberContacts.push({
                    ContactValue: contact.ContactValue,
                    ExtendedValue: country.CountryCode /*.replace('+', '')*/,
                    CountryId: contact.CountryId,
                });
            });

            this.smsService.sendSMS(this.sms).then(() => {
                loading();
                this.saveChangesWarningService.stop();
                this.stateService.go(FiStateNames.SMS.List);
            }, loading);
        }, loading);
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    isFirstContactNumber(index: number): boolean {
        return index === 0;
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
